import { ArrowLeft, FileText, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Separator } from './ui/separator';

interface TermsConditionsPageProps {
  onBack: () => void;
}

export default function TermsConditionsPage({ onBack }: TermsConditionsPageProps) {
  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-rose-700 to-amber-600 text-white px-4 py-3 flex items-center gap-3 sticky top-0 z-40 shadow-md">
        <Button variant="ghost" size="icon" className="text-white hover:bg-white/20" onClick={onBack}>
          <ArrowLeft className="size-6" />
        </Button>
        <span className="text-lg font-medium">Terms & Conditions</span>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto p-4 md:p-8 space-y-6">
          {/* Introduction */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="bg-rose-100 p-2 rounded-lg">
                  <FileText className="size-6 text-rose-600" />
                </div>
                <div>
                  <CardTitle>Terms of Service</CardTitle>
                  <CardDescription>Last updated: November 22, 2025</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700">
                Welcome to Atootbandan! These Terms and Conditions outline the rules and regulations for the use of our matrimonial platform. By accessing or using Atootbandan, you agree to be bound by these terms.
              </p>
            </CardContent>
          </Card>

          {/* Key Points */}
          <Card>
            <CardHeader>
              <CardTitle>Key Points</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {[
                'You must be 18 years or older to use this service',
                'All information provided must be accurate and truthful',
                'Respectful behavior is mandatory for all users',
                'Premium subscriptions are non-refundable unless specified',
                'We reserve the right to suspend accounts that violate our policies'
              ].map((point, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle className="size-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700">{point}</p>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Detailed Sections */}
          <Card>
            <CardHeader>
              <CardTitle>1. Account Registration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">1.1 Eligibility</h4>
                <p className="text-gray-700">
                  You must be at least 18 years of age (or the age of majority in your jurisdiction) to create an account on Atootbandan. By creating an account, you represent and warrant that you meet this eligibility requirement.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">1.2 Account Information</h4>
                <p className="text-gray-700">
                  You agree to provide accurate, current, and complete information during registration. You are responsible for maintaining the confidentiality of your account credentials and for all activities under your account.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">1.3 Verification</h4>
                <p className="text-gray-700">
                  We may require verification of your identity through government-issued ID, phone number, or other means. Verified profiles receive a verification badge and enhanced features.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">1.4 Report User</h4>
                <p className="text-gray-700">
                  If a user is found to be engaging in any inappropriate behavior with another user, you can report them in the chat section for that user.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>2. User Conduct</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">2.1 Prohibited Activities</h4>
                <p className="text-gray-700 mb-3">Users are strictly prohibited from:</p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>Providing false or misleading information</li>
                  <li>Harassing, threatening, or abusing other users</li>
                  <li>Using the platform for commercial solicitation</li>
                  <li>Sharing inappropriate or offensive content</li>
                  <li>Creating multiple accounts to circumvent restrictions</li>
                  <li>Attempting to access other users' accounts</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">2.2 Content Guidelines</h4>
                <p className="text-gray-700">
                  All photos, text, and other content you upload must comply with our community guidelines. We reserve the right to remove any content that violates these guidelines without notice.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>3. Premium Services & Payments</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">3.1 Subscription Plans</h4>
                <p className="text-gray-700">
                  Atootbandan offers various premium subscription plans that provide additional features. Subscription fees are charged in advance and are non-refundable except as required by law.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">3.2 Auto-Renewal</h4>
                <p className="text-gray-700">
                  Premium subscriptions automatically renew at the end of each billing period unless you cancel before the renewal date. You can manage your subscription in your account settings.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">3.3 Refund Policy</h4>
                <p className="text-gray-700">
                  Refunds are generally not provided for premium subscriptions. However, if you believe you were incorrectly charged, please contact our support team within 7 days of the charge.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>4. Privacy & Data Protection</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">4.1 Data Collection</h4>
                <p className="text-gray-700">
                  We collect and process personal data as described in our Privacy Policy. By using Atootbandan, you consent to such collection and processing.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">4.2 Profile Visibility</h4>
                <p className="text-gray-700">
                  Your profile information will be visible to other users of the platform. You can control visibility settings through your privacy preferences.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>5. Intellectual Property</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-700">
                All content, features, and functionality of Atootbandan are owned by us and are protected by international copyright, trademark, and other intellectual property laws. You may not copy, modify, distribute, or reproduce any part of our platform without express written permission.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>6. Limitation of Liability</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">6.1 No Guarantee</h4>
                <p className="text-gray-700">
                  Atootbandan does not guarantee that you will find a suitable match or life partner. The platform is provided "as is" without warranties of any kind.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">6.2 User Interactions</h4>
                <p className="text-gray-700">
                  We are not responsible for the conduct of any user. Users are solely responsible for their interactions with other users, both online and offline.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">6.3 Damages</h4>
                <p className="text-gray-700">
                  To the maximum extent permitted by law, Atootbandan shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of the platform.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>7. Account Termination</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">7.1 By You</h4>
                <p className="text-gray-700">
                  You may delete your account at any time through your account settings. Upon deletion, your profile will be removed from the platform within 30 days. You can write us on support@atootbandan.com from your registered email id to delete your account.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">7.2 By Us</h4>
                <p className="text-gray-700">
                  We reserve the right to suspend or terminate your account if you violate these Terms and Conditions or engage in fraudulent, illegal, or harmful behavior.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>8. Child Safety Policy</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-700">
                At Atootbandan, protecting children and preventing any form of child sexual abuse and exploitation (CSAE) is a top priority. We maintain a zero-tolerance policy toward any content, behavior, or activity that involves or promotes the use of our products by minors, and or promotes sexual exploitation or abuse of minors.
              </p>
              <p className="text-gray-700">
                We are fully committed to complying with all applicable child safety laws and regulations, and we actively cooperate with law enforcement agencies and relevant child protection organisations when necessary.
              </p>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Our standards prohibit:</h4>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>The use of our product by minors</li>
                  <li>Any form of child sexual abuse or exploitation (CSAE)</li>
                  <li>Sharing, uploading, or distributing child sexual abuse material (CSAM)</li>
                  <li>Soliciting or attempting to engage with minors</li>
                  <li>Misrepresenting age or identity</li>
                </ul>
              </div>
              <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                <p className="text-gray-700 mb-2">
                  If you encounter any behavior or content that may involve the exploitation or endangerment of a child, please report it immediately through our in-app reporting tools or by contacting our Child Safety team directly.
                </p>
                <p className="text-gray-900">
                  <span className="font-semibold">Child Safety Point of Contact:</span>{' '}
                  <a href="mailto:support@atootbandan.com" className="text-red-600 hover:underline">
                    support@atootbandan.com
                  </a>
                </p>
                <p className="text-sm text-gray-600 mt-2">
                  This contact is dedicated to addressing inquiries or reports related to child safety, CSAE prevention practices, and compliance with child protection policies.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>9. Changes to Terms</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-700">
                We may update these Terms and Conditions from time to time. We will notify you of any material changes by posting the new terms on this page and updating the "Last Updated" date. Your continued use of the platform after changes constitutes acceptance of the updated terms.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>10. Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-700">
                If you have any questions about these Terms and Conditions, please contact us at:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <p><span className="font-semibold">Email:</span> support@atootbandan.com</p>
              </div>
            </CardContent>
          </Card>

          <Separator className="my-6" />

          <Card className="bg-gradient-to-r from-rose-50 to-amber-50 border-rose-200">
            <CardContent className="pt-6">
              <p className="text-center text-gray-700">
                By creating an account or using Atootbandan, you acknowledge that you have read, understood, 
                and agree to be bound by these Terms and Conditions.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}